import { ValueOf } from '..';
import {
  RuleCategory,
  SiteClassification,
  TimeRestrictions,
  WebDomainRules,
} from '../api/types';
import { SafeNetworksStore } from './safeNetworks';
import { ScreenTime } from './screenTime';
import { ScreenTimeEvent } from '../api/events';
import { ConsoleEvent } from '../../logging/console';
import type { RoutineRule } from '..';

export { ROUTINE_RULE_TYPE } from '..';

export type {
  ContentFilteringRoutineRule,
  RoutineRule,
  RoutineRuleDefaultDomainCategoriesAction,
  RoutineRuleType,
  ScreenFreeTimeRoutineRule,
} from '..';

export type StoreMethods<T extends ValueOf<Stores>> = {
  set: (values: T extends object ? Partial<T> : T) => Promise<void>;
  get: () => T | undefined;
  clear: () => Promise<void>;
};

export enum StoresKeys {
  CREDENTIALS = 'qustodio:credentials',
  ACCOUNT = 'qustodio:account',
  DEVICE_AND_USER = 'qustodio:device_user',
  RULES = 'qustodio:rules',
  TIMES = 'qustodio:times',
  CLASSIFICATIONS = 'qustodio:classifications_lru',
  API = 'qustodio:api',
  DEVICE = 'qustodio:device',
  SAFE_NETWORKS = 'qustodio:safe_networks',
  PLATFORM_INFO = 'qustodio:platform_info',
  DELEGATION = 'qustodio:delegation',
  SCREEN_TIME = 'qustodio:screen_time',
  REPORTING = 'qustodio:reporting',
  REPORTED_DEVICE_INFO = 'qustodio:reported_device_info',
  TIME_NOTIFICATIONS = 'qustodio:time_notifications',
  LOGS = 'qustodio:logs',
}

export interface Stores {
  [StoresKeys.CREDENTIALS]: CredentialsStore;
  [StoresKeys.ACCOUNT]: AccountStore;
  [StoresKeys.DEVICE_AND_USER]: DeviceAndUserStore;
  [StoresKeys.RULES]: RulesStore;
  [StoresKeys.TIMES]: TimeRestrictions;
  [StoresKeys.CLASSIFICATIONS]: ClassificationStore;
  [StoresKeys.API]: ApiStore;
  [StoresKeys.DEVICE]: DeviceStore;
  [StoresKeys.SAFE_NETWORKS]: SafeNetworksStore;
  [StoresKeys.PLATFORM_INFO]: PlatformInfoStore;
  [StoresKeys.DELEGATION]: DelegationStore;
  [StoresKeys.SCREEN_TIME]: ScreenTimeStore;
  [StoresKeys.REPORTING]: ReportingStore;
  [StoresKeys.REPORTED_DEVICE_INFO]: ReportedDeviceInfoStore;
  [StoresKeys.TIME_NOTIFICATIONS]: TimeNotificationsStore;
  [StoresKeys.LOGS]: LogsStore;
}

export type AccountStore = {
  accountId: number;
  accountUid: string;
  masterAccountId: number | null;
  masterAccountUid: string | null;
  locale: string;
};

export type CredentialsStore = {
  accessToken: string;
  refreshToken: string;
};

export type FCMInfo = {
  pushToken?: string;
  lastSentToken?: string;
};

export type DeviceAndUserStore = {
  device: string;
  user: string;
  profileId: number;
  profileName: string;
  consumedQuotaSeconds: number;
  fcm?: FCMInfo;
};

export type RulesStore = {
  timestamp: string;
  timezone: string;
  timeRestrictions: TimeRestrictionsStore;
  categories: RuleCategory[];
  allowUnknownSites: boolean;
  isCategoryList: boolean;
  isSafeSearch: boolean;
  domainRules: WebDomainRules;
  safeNetworksRules: SafeNetworksRules;
  /**
   * Optional so persisted stores created before quota rollout remain readable.
   * Store getters provide the default shape expected by consumers.
   */
  dailyQuota?: {
    extraTimeMinutes: number;
    extraTimeEndsAtInSeconds?: number;
  };
  /**
   * Optional so persisted stores created before routines rollout remain
   * readable. Store getters provide the default shape expected by consumers.
   */
  routines?: RoutineRule[];
  /**
   * Epoch seconds of the next planned screen-free-time routine start (i.e.
   * a `LOCK_NAVIGATION` / `LOCK_DEVICE` routine). Computed at sync time
   * from the raw API schedules so the routine checker can expose the
   * `noRoutineActiveButExpectedSoon` state without re-deriving it from
   * truncated data.
   */
  nextScreenFreeTimeRoutineStartsAtInSeconds?: number;
  /**
   * Extension version that produced this snapshot, used to force a re-parse
   * after an upgrade (see `shouldForceRulesRefreshAfterExtensionUpdate`).
   * Optional so snapshots persisted before this field remain readable.
   */
  lastSyncedExtensionVersion?: string;
};

export type SafeNetworksRules = {
  delegateProtectionInSafeNetwork: boolean;
};

export type PauseInternetRuleStateStore = {
  endsAtInSeconds: number;
};

/**
 * Store-side mirror of the API `TimeRestrictions`. `pause_internet` is the
 * only field that diverges: the conversion happens once in `fetchRules.ts`,
 * the same way `dailyQuota` is normalized at fetch time, so consumers of the
 * store never see the raw `ends_at: number | string` shape.
 */
export type TimeRestrictionsStore = Omit<TimeRestrictions, 'pause_internet'> & {
  pause_internet?: PauseInternetRuleStateStore;
};

export type ClassificationStore = {
  classifications: {
    key: string;
    value: SiteClassification;
  }[];
};

export type LogsStore = {
  logs: {
    key: string;
    value: ConsoleEvent;
  }[];
};

export type ApiStore = {
  lastUpdateLastSeen: string;
};

export type DeviceStore = {
  /** Is delegated by Safe Network */
  isProtectionDelegated: boolean;
  lastNetworkConnectionType: ConnectionType;
};

export type PlatformInfoStore = {
  arch: string;
  os: string;
  osVersion?: string;
};

export type DelegationStore = {
  isLoggedIn: boolean;
  isDelegationStarted: boolean;
};

export type ScreenTimeStore = {
  screenTime: ScreenTime;
};

export type ReportingStore = {
  timestamp: number; // last timestamp reporting with success
  events: {
    screenTime: ScreenTimeEvent[];
  };
};

export type ReportedDeviceInfoStore = {
  extensionVersion: string;
  osVersion: string;
  isDelegationStarted: boolean;
};

export type TimeNotificationsStore = {
  isNotifiedDailyQuotaExceeded: boolean;
  isNotified5MinutesForDailyQuota: boolean;
  isNotified15MinutesForDailyQuota: boolean;
  lastNotifiedPauseEndsAt?: number;
  lastNotifiedRoutineActivation?: string;
  /**
   * Stores `routineStartsAtInSeconds` for which we have already shown the
   * 15-minute pre-start notification. Survives service-worker restarts so
   * delegation devices do not double-notify when an alarm refires after
   * a SW boot.
   */
  lastNotifiedRoutinePreStart15Min?: number;
  /**
   * Same idempotency idea as `lastNotifiedRoutinePreStart15Min`, scoped to
   * the 5-minute pre-start notification.
   */
  lastNotifiedRoutinePreStart5Min?: number;
};
